import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload,
  Users,
  Phone,
  Mail,
  Building2,
  Search,
  MoreVertical,
  Pencil,
  Trash2,
  Plus,
  FileSpreadsheet,
  Tag,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  PhoneOff,
  Filter,
  Download,
  Loader2,
  ChevronRight,
  Flame,
  ThermometerSun,
  Snowflake
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";

const API = `${process.env.REACT_APP_API_URL}/api`;

const CALL_STATUS_ICONS = {
  not_called: { icon: Clock, color: "text-muted-foreground", label: "Not Called" },
  answered: { icon: CheckCircle, color: "text-emerald-400", label: "Answered" },
  no_answer: { icon: XCircle, color: "text-amber-400", label: "No Answer" },
  busy: { icon: Phone, color: "text-orange-400", label: "Busy" },
  voicemail: { icon: Phone, color: "text-blue-400", label: "Voicemail" },
  scheduled: { icon: Clock, color: "text-purple-400", label: "Scheduled" },
  failed: { icon: AlertTriangle, color: "text-red-400", label: "Failed" }
};

const LEAD_SCORE_ICONS = {
  hot: { icon: Flame, color: "text-red-400", label: "Hot Lead" },
  warm: { icon: ThermometerSun, color: "text-amber-400", label: "Warm Lead" },
  cold: { icon: Snowflake, color: "text-blue-400", label: "Cold Lead" },
  not_interested: { icon: XCircle, color: "text-muted-foreground", label: "Not Interested" }
};

export default function Phonebook() {
  const { getAuthHeader } = useAuth();
  const fileInputRef = useRef(null);
  
  // State
  const [contacts, setContacts] = useState([]);
  const [lists, setLists] = useState([]);
  const [dncList, setDncList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [totalContacts, setTotalContacts] = useState(0);
  
  // Filters
  const [selectedList, setSelectedList] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [callStatusFilter, setCallStatusFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 20;
  
  // Dialogs
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dncDialogOpen, setDncDialogOpen] = useState(false);
  const [importResultDialog, setImportResultDialog] = useState(false);
  
  // Form data
  const [editingContact, setEditingContact] = useState(null);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [uploadListName, setUploadListName] = useState("Imported Contacts");
  const [importResult, setImportResult] = useState(null);
  const [newDncPhone, setNewDncPhone] = useState("");
  const [newDncReason, setNewDncReason] = useState("");

  useEffect(() => {
    fetchLists();
    fetchContacts();
    fetchDncList();
  }, []);

  useEffect(() => {
    fetchContacts();
  }, [selectedList, searchQuery, callStatusFilter, currentPage]);

  const fetchLists = async () => {
    try {
      const res = await axios.get(`${API}/phonebook/lists`, { headers: getAuthHeader() });
      setLists(res.data.lists || []);
    } catch (error) {
      console.error("Failed to fetch lists:", error);
    }
  };

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedList) params.append("list_name", selectedList);
      if (searchQuery) params.append("search", searchQuery);
      if (callStatusFilter) params.append("call_status", callStatusFilter);
      params.append("skip", currentPage * pageSize);
      params.append("limit", pageSize);
      
      const res = await axios.get(`${API}/phonebook/contacts?${params}`, { headers: getAuthHeader() });
      setContacts(res.data.contacts || []);
      setTotalContacts(res.data.total || 0);
    } catch (error) {
      console.error("Failed to fetch contacts:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDncList = async () => {
    try {
      const res = await axios.get(`${API}/phonebook/dnc`, { headers: getAuthHeader() });
      setDncList(res.data.entries || []);
    } catch (error) {
      console.error("Failed to fetch DNC list:", error);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error("Please upload a CSV file");
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("list_name", uploadListName);
      formData.append("skip_duplicates", "true");
      formData.append("skip_dnc", "true");
      
      const res = await axios.post(`${API}/phonebook/import`, formData, {
        headers: {
          ...getAuthHeader(),
          "Content-Type": "multipart/form-data"
        }
      });
      
      setImportResult(res.data);
      setImportResultDialog(true);
      setUploadDialogOpen(false);
      
      // Refresh data
      fetchLists();
      fetchContacts();
      
      toast.success(`Imported ${res.data.imported} contacts`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleEditContact = async () => {
    if (!editingContact) return;
    
    try {
      await axios.put(
        `${API}/phonebook/contacts/${editingContact.id}`,
        editingContact,
        { headers: getAuthHeader() }
      );
      
      toast.success("Contact updated");
      setEditDialogOpen(false);
      setEditingContact(null);
      fetchContacts();
    } catch (error) {
      toast.error("Failed to update contact");
    }
  };

  const handleDeleteContact = async () => {
    if (!contactToDelete) return;
    
    try {
      await axios.delete(
        `${API}/phonebook/contacts/${contactToDelete.id}`,
        { headers: getAuthHeader() }
      );
      
      toast.success("Contact deleted");
      setDeleteDialogOpen(false);
      setContactToDelete(null);
      fetchContacts();
      fetchLists();
    } catch (error) {
      toast.error("Failed to delete contact");
    }
  };

  const handleAddToDnc = async (phone, reason = null) => {
    try {
      await axios.post(
        `${API}/phonebook/dnc`,
        { phone, reason },
        { headers: getAuthHeader() }
      );
      
      toast.success("Added to Do Not Call list");
      fetchContacts();
      fetchDncList();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add to DNC");
    }
  };

  const handleRemoveFromDnc = async (phone) => {
    try {
      await axios.delete(
        `${API}/phonebook/dnc/${encodeURIComponent(phone)}`,
        { headers: getAuthHeader() }
      );
      
      toast.success("Removed from Do Not Call list");
      fetchDncList();
      fetchContacts();
    } catch (error) {
      toast.error("Failed to remove from DNC");
    }
  };

  const totalPages = Math.ceil(totalContacts / pageSize);

  return (
    <div className="p-8" data-testid="phonebook-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight mb-2">
            Phonebook
          </h1>
          <p className="text-muted-foreground">
            Manage contacts for outbound calling campaigns
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => setDncDialogOpen(true)}
            data-testid="manage-dnc-btn"
          >
            <PhoneOff className="w-4 h-4 mr-2" />
            DNC List ({dncList.length})
          </Button>
          <Button 
            onClick={() => setUploadDialogOpen(true)}
            data-testid="upload-csv-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import CSV
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold">{totalContacts}</p>
                <p className="text-sm text-muted-foreground">Total Contacts</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {contacts.filter(c => c.call_status === "answered").length}
                </p>
                <p className="text-sm text-muted-foreground">Contacted</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {contacts.filter(c => c.call_status === "not_called").length}
                </p>
                <p className="text-sm text-muted-foreground">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <Flame className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {contacts.filter(c => c.lead_score === "hot").length}
                </p>
                <p className="text-sm text-muted-foreground">Hot Leads</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search contacts..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="search-contacts"
                />
              </div>
            </div>
            <Select value={selectedList || "all"} onValueChange={(v) => setSelectedList(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[200px]" data-testid="filter-list">
                <SelectValue placeholder="All Lists" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Lists</SelectItem>
                {lists.map((list) => (
                  <SelectItem key={list.name} value={list.name}>
                    {list.name} ({list.total})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={callStatusFilter || "all"} onValueChange={(v) => setCallStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[180px]" data-testid="filter-status">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="not_called">Not Called</SelectItem>
                <SelectItem value="answered">Answered</SelectItem>
                <SelectItem value="no_answer">No Answer</SelectItem>
                <SelectItem value="voicemail">Voicemail</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Contact Lists Sidebar + Contacts Table */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Lists Sidebar */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Contact Lists</CardTitle>
          </CardHeader>
          <CardContent className="p-2">
            <div className="space-y-1">
              <button
                className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                  selectedList === "" ? "bg-primary/10 text-primary" : "hover:bg-muted"
                }`}
                onClick={() => setSelectedList("")}
              >
                <div className="flex items-center justify-between">
                  <span>All Contacts</span>
                  <Badge variant="secondary">{totalContacts}</Badge>
                </div>
              </button>
              {lists.map((list) => (
                <button
                  key={list.name}
                  className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                    selectedList === list.name ? "bg-primary/10 text-primary" : "hover:bg-muted"
                  }`}
                  onClick={() => setSelectedList(list.name)}
                >
                  <div className="flex items-center justify-between">
                    <span className="truncate">{list.name}</span>
                    <Badge variant="secondary">{list.total}</Badge>
                  </div>
                  <div className="flex gap-2 mt-1 text-xs text-muted-foreground">
                    <span>{list.not_called} pending</span>
                    <span>•</span>
                    <span>{list.called} called</span>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Contacts Table */}
        <Card className="lg:col-span-3">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : contacts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Users className="w-12 h-12 text-muted-foreground mb-4" />
                <h3 className="font-semibold mb-2">No contacts yet</h3>
                <p className="text-muted-foreground mb-4">Import contacts from a CSV file to get started</p>
                <Button onClick={() => setUploadDialogOpen(true)}>
                  <Upload className="w-4 h-4 mr-2" />
                  Import CSV
                </Button>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Lead Score</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contacts.map((contact) => {
                      const statusInfo = CALL_STATUS_ICONS[contact.call_status] || CALL_STATUS_ICONS.not_called;
                      const StatusIcon = statusInfo.icon;
                      const scoreInfo = contact.lead_score ? LEAD_SCORE_ICONS[contact.lead_score] : null;
                      const ScoreIcon = scoreInfo?.icon;
                      
                      return (
                        <TableRow key={contact.id} data-testid={`contact-row-${contact.id}`}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{contact.name}</p>
                              {contact.email && (
                                <p className="text-xs text-muted-foreground">{contact.email}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="font-mono text-sm">{contact.phone}</TableCell>
                          <TableCell>{contact.company || "-"}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={statusInfo.color}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusInfo.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {scoreInfo ? (
                              <Badge variant="outline" className={scoreInfo.color}>
                                <ScoreIcon className="w-3 h-3 mr-1" />
                                {scoreInfo.label}
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => {
                                  setEditingContact(contact);
                                  setEditDialogOpen(true);
                                }}>
                                  <Pencil className="w-4 h-4 mr-2" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleAddToDnc(contact.phone, "User request")}>
                                  <PhoneOff className="w-4 h-4 mr-2" />
                                  Add to DNC
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  className="text-destructive"
                                  onClick={() => {
                                    setContactToDelete(contact);
                                    setDeleteDialogOpen(true);
                                  }}
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between p-4 border-t">
                    <p className="text-sm text-muted-foreground">
                      Showing {currentPage * pageSize + 1}-{Math.min((currentPage + 1) * pageSize, totalContacts)} of {totalContacts}
                    </p>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled={currentPage === 0}
                        onClick={() => setCurrentPage(p => p - 1)}
                      >
                        Previous
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        disabled={currentPage >= totalPages - 1}
                        onClick={() => setCurrentPage(p => p + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Upload CSV Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5" />
              Import Contacts from CSV
            </DialogTitle>
            <DialogDescription>
              Upload a CSV file with your contacts. Required columns: name, phone
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>List Name</Label>
              <Input
                value={uploadListName}
                onChange={(e) => setUploadListName(e.target.value)}
                placeholder="e.g., Sales Leads Q1"
                data-testid="list-name-input"
              />
            </div>
            
            <div className="p-6 border-2 border-dashed rounded-lg text-center">
              <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground mb-4">
                Drag & drop a CSV file or click to browse
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                className="hidden"
                onChange={handleFileUpload}
                data-testid="csv-file-input"
              />
              <Button 
                variant="outline" 
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                {uploading ? "Uploading..." : "Select CSV File"}
              </Button>
            </div>
            
            <div className="text-xs text-muted-foreground">
              <p className="font-medium mb-1">Expected CSV format:</p>
              <code className="block p-2 bg-muted rounded">
                name,phone,email,company,tags,notes
              </code>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Result Dialog */}
      <Dialog open={importResultDialog} onOpenChange={setImportResultDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-400" />
              Import Complete
            </DialogTitle>
          </DialogHeader>
          
          {importResult && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-emerald-500/10 rounded-lg text-center">
                  <p className="text-2xl font-bold text-emerald-400">{importResult.imported}</p>
                  <p className="text-sm text-muted-foreground">Imported</p>
                </div>
                <div className="p-3 bg-muted rounded-lg text-center">
                  <p className="text-2xl font-bold">{importResult.total_rows}</p>
                  <p className="text-sm text-muted-foreground">Total Rows</p>
                </div>
              </div>
              
              {(importResult.skipped_duplicates > 0 || importResult.skipped_dnc > 0 || importResult.skipped_invalid > 0) && (
                <div className="space-y-2">
                  <p className="text-sm font-medium">Skipped:</p>
                  <div className="text-sm text-muted-foreground space-y-1">
                    {importResult.skipped_duplicates > 0 && (
                      <p>• {importResult.skipped_duplicates} duplicates</p>
                    )}
                    {importResult.skipped_dnc > 0 && (
                      <p>• {importResult.skipped_dnc} in DNC list</p>
                    )}
                    {importResult.skipped_invalid > 0 && (
                      <p>• {importResult.skipped_invalid} invalid entries</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setImportResultDialog(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Contact Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Contact</DialogTitle>
          </DialogHeader>
          
          {editingContact && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input
                  value={editingContact.name}
                  onChange={(e) => setEditingContact({...editingContact, name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={editingContact.phone}
                  onChange={(e) => setEditingContact({...editingContact, phone: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  value={editingContact.email || ""}
                  onChange={(e) => setEditingContact({...editingContact, email: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Company</Label>
                <Input
                  value={editingContact.company || ""}
                  onChange={(e) => setEditingContact({...editingContact, company: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={editingContact.notes || ""}
                  onChange={(e) => setEditingContact({...editingContact, notes: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Lead Score</Label>
                <Select
                  value={editingContact.lead_score || "none"}
                  onValueChange={(v) => setEditingContact({...editingContact, lead_score: v === "none" ? null : v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Not scored" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not Scored</SelectItem>
                    <SelectItem value="hot">Hot Lead</SelectItem>
                    <SelectItem value="warm">Warm Lead</SelectItem>
                    <SelectItem value="cold">Cold Lead</SelectItem>
                    <SelectItem value="not_interested">Not Interested</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleEditContact}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Contact</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{contactToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteContact} className="bg-destructive">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* DNC List Dialog */}
      <Dialog open={dncDialogOpen} onOpenChange={setDncDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PhoneOff className="w-5 h-5" />
              Do Not Call List
            </DialogTitle>
            <DialogDescription>
              Phone numbers that will be skipped during imports and campaigns
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Add to DNC */}
            <div className="flex gap-2">
              <Input
                placeholder="+1234567890"
                value={newDncPhone}
                onChange={(e) => setNewDncPhone(e.target.value)}
              />
              <Button onClick={async () => {
                if (newDncPhone) {
                  await handleAddToDnc(newDncPhone, newDncReason);
                  setNewDncPhone("");
                  setNewDncReason("");
                }
              }}>
                Add
              </Button>
            </div>
            
            {/* DNC List */}
            <div className="max-h-[300px] overflow-y-auto space-y-2">
              {dncList.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">
                  No numbers in DNC list
                </p>
              ) : (
                dncList.map((entry) => (
                  <div 
                    key={entry.id} 
                    className="flex items-center justify-between p-2 bg-muted/50 rounded"
                  >
                    <div>
                      <p className="font-mono text-sm">{entry.phone}</p>
                      {entry.reason && (
                        <p className="text-xs text-muted-foreground">{entry.reason}</p>
                      )}
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleRemoveFromDnc(entry.phone)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
